// supportDiagnostics — pure helpery trybu serwisowego (KROK 1, 2026-08-18).
// Osobny .js (nie .jsx) żeby node:test mógł importować bez transpilacji
// (wzorzec forceUpload.js z ForceUploadButton).

// Blob/bytes nie serializują się do JSON → metadane (wzór AdminQueueScreen.toSafeEntry).
// R2b-a: wpisy bajtowe mają blobSize/blobType w rekordzie; `bytes` (ArrayBuffer)
// MUSI wypaść z payloadu (JSON zrobiłby z niego {} albo gigantyczny obiekt).
// V3 B12e: `thumbBytes` (miniatura ⏳) też wypada z payloadu.
export function toSafeEntry({ blob, bytes, thumbBytes, ...rest }) {
  return {
    ...rest,
    blobSize: rest.blobSize ?? bytes?.byteLength ?? blob?.size ?? null,
    blobType: rest.blobType ?? blob?.type ?? null,
    createdAtIso: rest.createdAt != null ? new Date(rest.createdAt).toISOString() : null,
  };
}

// R2b-b: wpis rejestru „Utracone" do payloadu diagnostyki (metadane + ISO czasy).
export function toLostEntry(entry) {
  return {
    ...entry,
    createdAtIso: entry.createdAt != null ? new Date(entry.createdAt).toISOString() : null,
    lostAtIso: entry.lostAt != null ? new Date(entry.lostAt).toISOString() : null,
  };
}

// V3 B6: wpis rejestru prób wysyłki do payloadu diagnostyki (ISO czas, bez binariów).
export function toUploadLogEntry(entry) {
  return {
    ...entry,
    atIso: entry?.at != null ? new Date(entry.at).toISOString() : null,
  };
}

/**
 * v73 (B-1b): pytanie KONKRETNEGO workera (kontroler / installing / waiting) o wersję
 * i licznik precache przez MessageChannel. null gdy brak workera albo timeout.
 * sw.js v72: { version, precached, expected }; v73: + installState, otherCaches.
 */
export function querySwWorker(worker, timeoutMs = 1500) {
  return new Promise((resolve) => {
    try {
      if (!worker || typeof worker.postMessage !== 'function') return resolve(null);
      const ch = new MessageChannel();
      const timer = setTimeout(() => resolve(null), timeoutMs);
      let base = null;
      let settle = null;
      ch.port1.onmessage = (ev) => {
        const d = ev.data || {};
        if (!d.version) return;
        if (d.precached !== undefined) {
          clearTimeout(timer); clearTimeout(settle);
          resolve({
            version: d.version, precached: d.precached ?? null, expected: d.expected ?? null,
            installState: d.installState ?? null, otherCaches: Array.isArray(d.otherCaches) ? d.otherCaches : [],
          });
          return;
        }
        // Pierwsza odpowiedź (sama wersja, kontrakt KROK 1) — chwila na dopełnienie.
        base = { version: d.version, precached: null, expected: null, installState: null, otherCaches: [] };
        clearTimeout(settle);
        settle = setTimeout(() => { clearTimeout(timer); resolve(base); }, 400);
      };
      worker.postMessage({ type: 'GET_VERSION' }, [ch.port2]);
    } catch {
      resolve(null);
    }
  });
}

// V3: pełna odpowiedź RUNNING SW (sw.js v72 GET_VERSION: { version, precached, expected }).
// null gdy brak kontrolera (pierwsze otwarcie / desktop bez SW) albo timeout.
export function fetchSwInfo(timeoutMs = 1500) {
  try {
    const ctrl = navigator.serviceWorker?.controller;
    if (!ctrl) return Promise.resolve(null);
    return querySwWorker(ctrl, timeoutMs);
  } catch {
    return Promise.resolve(null);
  }
}

/**
 * v73 (B-1b, F3): stan rejestracji SW z perspektywy skorupy — kontroler (aktywny),
 * installing (precache w toku) i waiting (zainstalowany, czeka). Każdy pytany o wersję.
 * @returns {Promise<{active:object|null, installing:object|null, waiting:object|null, hasRegistration:boolean}>}
 */
export async function fetchSwRegistrationInfo(timeoutMs = 1500) {
  const out = { active: null, installing: null, waiting: null, hasRegistration: false };
  try {
    const sw = navigator.serviceWorker;
    if (!sw) return out;
    const [active, reg] = await Promise.all([
      fetchSwInfo(timeoutMs),
      sw.getRegistration ? sw.getRegistration().catch(() => null) : Promise.resolve(null),
    ]);
    out.active = active;
    if (reg) {
      out.hasRegistration = true;
      const [inst, wait] = await Promise.all([
        reg.installing ? querySwWorker(reg.installing, timeoutMs) : Promise.resolve(null),
        reg.waiting ? querySwWorker(reg.waiting, timeoutMs) : Promise.resolve(null),
      ]);
      out.installing = inst;
      out.waiting = wait;
    }
  } catch { /* nic */ }
  return out;
}

// Wersja RUNNING service workera przez MessageChannel (sw.js v43: GET_VERSION).
// null gdy brak kontrolera (pierwsze otwarcie / desktop bez SW) albo timeout.
export function fetchSwVersion(timeoutMs = 1500) {
  return fetchSwInfo(timeoutMs).then((i) => i?.version || null);
}
